// The Ask Amari conversation. It stays on this device for the browser session only (sessionStorage):
// it isn't synced, and the server never stores it either.

import { create } from 'zustand';

export interface AskLine {
  id: string;
  from: 'me' | 'amari';
  text: string;
  /** Recipes Amari suggested (already checked against the candidates it was given). */
  recipeIds?: string[];
  /** Safety warnings the app added under Amari's message. */
  notes?: string[];
  /** A question that didn't get an answer; kept so it can be retried, never sent as history. */
  failed?: boolean;
  /** Amari is writing a new recipe for this message. */
  writing?: boolean;
  /** Offers under Amari's message: ingredient ids to remember as dislikes, or to put on the list. */
  offers?: { avoid: string[]; list: string[] };
  /** Offers already taken. */
  done?: ('avoid' | 'list')[];
}

interface AskState {
  lines: AskLine[];
  quota: { used: number; limit: number } | null;
  add: (line: Omit<AskLine, 'id'>) => string;
  update: (id: string, patch: Partial<AskLine>) => void;
  remove: (id: string) => void;
  setQuota: (q: { used: number; limit: number }) => void;
  clear: () => void;
}

const KEY = 'amari_ask';
const MAX_LINES = 60;

function load(): Pick<AskState, 'lines' | 'quota'> {
  try {
    const raw = JSON.parse(sessionStorage.getItem(KEY) ?? 'null') as Pick<AskState, 'lines' | 'quota'> | null;
    if (raw && Array.isArray(raw.lines)) {
      return {
        lines: raw.lines.filter((l) => l && typeof l.id === 'string' && (l.from === 'me' || l.from === 'amari') && typeof l.text === 'string').slice(-MAX_LINES),
        quota: raw.quota && typeof raw.quota.used === 'number' ? raw.quota : null,
      };
    }
  } catch { /* private mode or bad data: start fresh */ }
  return { lines: [], quota: null };
}

let seq = 0;
const newId = () => `${Date.now().toString(36)}-${(seq++).toString(36)}`;

export const useAsk = create<AskState>((set) => ({
  ...load(),
  add: (line) => {
    const id = newId();
    set((s) => ({ lines: [...s.lines, { ...line, id }].slice(-MAX_LINES) }));
    return id;
  },
  update: (id, patch) => set((s) => ({ lines: s.lines.map((l) => (l.id === id ? { ...l, ...patch } : l)) })),
  remove: (id) => set((s) => ({ lines: s.lines.filter((l) => l.id !== id) })),
  setQuota: (quota) => set({ quota }),
  clear: () => set({ lines: [] }),
}));

useAsk.subscribe((s) => {
  try { sessionStorage.setItem(KEY, JSON.stringify({ lines: s.lines, quota: s.quota })); } catch { /* full or private */ }
});
